import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface AppErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

function AppErrorFallback({ error, resetError }: AppErrorFallbackProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 text-destructive">
            <AlertTriangle className="h-full w-full" />
          </div>
          <CardTitle className="text-2xl">
            {t("errors.appError.title")}
          </CardTitle>
          <CardDescription>{t("errors.appError.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {import.meta.env.DEV && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">
                {t("errors.appError.details")}
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                {error instanceof Error ? error.message : String(error)}
                {error instanceof Error && error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
          <div className="flex gap-2">
            <Button onClick={resetError} className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t("errors.appError.tryAgain")}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="flex-1"
            >
              {t("errors.appError.reloadPage")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AppErrorBoundary({
  children,
  onReset,
}: {
  children: React.ReactNode;
  onReset?: () => void;
}) {
  return (
    <Sentry.ErrorBoundary fallback={AppErrorFallback} onReset={onReset}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
